import { relations, sql } from 'drizzle-orm'

import {
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { users } from './user'

export const notificationStatusEnum = pgEnum('notification_status', [
  'pending',
  'processing',
  'sent',
  'skipped',
  'failed',
  'bounced',
])

export const notifications = pgTable(
  'notifications',
  {
    ...getBaseFields(),

    // Who to notify
    userId: getIdReference(() => users.id),

    // What entity triggered this
    entityId: integer().notNull(), // ID of the entity
    notificationType: text().notNull(), // 'visit_feedback_request', 'subscription_renewal', etc.

    // When to send
    sendAt: getTimestampWithTimezone(), // NULL = immediate, otherwise scheduled
    sentAt: getTimestampWithTimezone(), // NULL = not sent yet

    // Status
    status: notificationStatusEnum().notNull().default('pending'),
    skipReason: text(), // Why it was skipped (e.g., 'already_rated')
    error: text(), // Error message if failed
    sendTryCount: integer().notNull().default(0), // How many times we've attempted to send

    // Content (simple)
    channel: text().notNull(), // 'email', 'push', 'sms'
    title: text(),
    body: text().notNull(),
    data: jsonb(), // Extra data for the notification (URLs, etc.)

    // Deduplication - prevents sending identical notifications
    deduplicationKey: text().notNull(), // Auto-generated or custom key

    // Delivery tracking
    externalMessageId: text(), // External ID from channel (SES message ID, etc.)
    deliveryResult: jsonb(), // Channel-specific delivery details

    // Open tracking (for newsletter emails)
    openedAt: getTimestampWithTimezone(),
  },
  (table) => ({
    ...getBaseIndexes('notifications', table),
    sendAtIndex: index('notifications_send_at_idx').on(table.sendAt),
    entityIndex: index('notifications_entity_idx').on(
      table.notificationType,
      table.entityId,
    ),
    statusIndex: index('notifications_status_idx').on(table.status),
    userIdIndex: index('notifications_user_id_idx').on(table.userId),
    deduplicationUniqueIndex: uniqueIndex('notifications_deduplication_idx')
      .on(table.deduplicationKey)
      .where(sql`deleted_at IS NULL`),
    externalMessageIdIndex: index('notifications_external_message_id_idx').on(
      table.externalMessageId,
    ),
  }),
)

export const notificationsRelations = relations(notifications, ({ one }) => ({
  user: one(users, {
    fields: [notifications.userId],
    references: [users.id],
  }),
}))
